import clsx from "clsx";
import { isNil } from "lodash";
import { FC } from "react";
import { useTranslation, UseTranslationResponse } from "react-i18next";
import { useRecoilValue } from "recoil";

import { useCrudNavigate } from "@ryde/redux";
import {
  appLanguageSelector,
  calculateDateDifference,
  INotificationDto,
  nowTDateISO,
  resolveNotificationContent,
  TDrawerCloseFunction,
  useTimezone,
} from "@ryde/shared";

import {
  INotificationItemFooterDefinition,
  NotificationItemFooter,
} from "./notification-item-footer";
import { NotificationItemHeader } from "./notification-item-header";
import { NotificationItemStyled } from "./notification-item.styled";

const resolveFooterItems = (
  notification: INotificationDto,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: UseTranslationResponse<any, any>["t"],
): INotificationItemFooterDefinition[] => {
  const result: INotificationItemFooterDefinition[] = [];

  if (!isNil(notification.unitId)) {
    result.push({
      title: t("notification.unitId"),
      description: String(notification.unitName),
    });
  }

  if (!isNil(notification.siteId)) {
    result.push({
      title: t("notification.siteId"),
      description: String(notification.siteName),
    });
  }

  if (!isNil(notification.metadata.supplierId)) {
    result.push({
      title: t("notification.supplierId"),
      description: String(notification.metadata.supplierId),
    });
  }

  if (!isNil(notification.metadata.businessUnitId)) {
    result.push({
      title: t("notification.businessUnitId"),
      description: String(notification.metadata.businessUnitId),
    });
  }

  result.push({
    title: t("notification.recipientId"),
    description: String(notification.recipientName),
  });

  return result;
};

interface INotificationItemProps {
  notification: INotificationDto;
  onCloseDrawer: TDrawerCloseFunction;
}

export const NotificationItem: FC<INotificationItemProps> = ({
  notification,
  onCloseDrawer,
}) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const language = useRecoilValue(appLanguageSelector);
  const title = notification.title;
  const details = resolveNotificationContent(notification.content, language);
  const time = calculateDateDifference(
    notification.createdAt,
    nowTDateISO(timezone),
    "hours",
    true,
  );
  const { navigateToEdit } = useCrudNavigate("notification");
  const footerItems = resolveFooterItems(notification, t);

  const onNotificationItemClick = (): void => {
    onCloseDrawer();
    navigateToEdit(notification.id);
  };

  return (
    <NotificationItemStyled
      className={clsx({
        NotificationItem_inactive: notification.isRead,
      })}
      onClick={onNotificationItemClick}
    >
      <NotificationItemHeader
        priority={notification.priority}
        title={title}
        time={time}
      />
      <div className="NotificationItem-Details">{details}</div>
      <NotificationItemFooter footerItems={footerItems} />
    </NotificationItemStyled>
  );
};
